'use client';

import { useSupabaseAuth } from '@/components/auth/SupabaseAuthProvider';
import { useResilientBootstrap } from '@/hooks/useResilientBootstrap';
import usersServices from '@/services/users-services';
import { readProfessionalMfaRequired } from '@/utils/professionalMfa';
import { createClient } from '@/utils/supabase/client';
import type { User as SupabaseAuthUser } from '@supabase/supabase-js';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

interface SupabaseUser {
  user_id: string;
  email: string;
  full_name: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  stripe_product_id: string | null;
  // Issue #3355 PR 2e — `stripe_plan_name` removed from the typed surface.
  // Backend stopped writing it (PR 1, PR 2d) and is dropping it from
  // `UserInfo` Pydantic model in this same PR; the DB column is removed
  // by PR 3. Frontend consumers route plan identity through the catalog
  // resolvers, not this column.
  stripe_subscription_status: string | null;
  created_at: string;
  updated_at: string;
  current_period_end: string | null;
  plan_tag: string | null;
  /**
   * Resolved capabilities map for the caller's current ``plan_tag``,
   * sourced from ``plans.capabilities`` by ``/users/me``. Empty object
   * when the user has no plan yet. Feature gates read flags from this
   * map (e.g. ``bundle_professional_profile``) — never hardcode plan
   * tags client-side.
   *
   * Values are boolean for on/off capabilities and number for quantitative
   * ones (e.g. ``doc_analysis_max_pages`` — issue #4732).
   */
  plan_capabilities: Record<string, boolean | number>;
  /**
   * Latched advocate-practice on-ramp (#6324). True once the user has selected
   * the *Advokát* profile type (or via the explicit "Zapnúť Moja kancelária"
   * toggle); stays true across later `profession` (persona) changes and turns
   * off only via an explicit, confirmed action. Decoupled from `profession`.
   *
   * This is the SELF-DECLARED half of practice visibility — `useAuthUser`'s
   * `practiceEnabled` getter ORs it with a claimed advocate profile
   * (`isProfessional`). Governs only the PRIVATE office; public/verified
   * features stay behind verification.
   */
  practice_self_enabled: boolean;
}

interface ActiveOrg {
  id: string;
  name: string;
  slug: string | null;
  role: string | null; // e.g. "org:owner" | "org:member"
  isAdmin: boolean;
  /**
   * `org_require_mfa` claim — true only once THIS member's grace period has
   * ended (#1887). Both middlewares block AAL1 on it; during grace it is
   * false and `mfaDeadline` says when it will flip.
   */
  mfaEnforced: boolean;
  /** `org_mfa_deadline` claim — ISO instant; null when the org does not require MFA. */
  mfaDeadline: string | null;
}

type AuthAssuranceLevel = 'aal1' | 'aal2' | null;

interface MfaState {
  /** Current assurance level (aal1 = password only, aal2 = MFA verified) */
  currentLevel: AuthAssuranceLevel;
  /** Next required level (aal2 if user has enrolled factors) */
  nextLevel: AuthAssuranceLevel;
  /** True when user has MFA factors but hasn't verified in this session */
  challengeRequired: boolean;
}

interface AuthContextType {
  // Auth data
  isAuthenticated: boolean;
  isLoading: boolean;
  authUser: SupabaseAuthUser | null;

  // Active organization (null when user has no active org)
  activeOrg: ActiveOrg | null;

  /**
   * `professional_require_mfa` claim — the SAK verdict the hook enforces
   * (#13125): a claimed advocate profile, a confirmed email found in the SAK
   * register, or a self-declared advocate past the rollout grace instant.
   * Both middlewares block AAL1 on it; the backend's `/users/me`-derived
   * `isProfessional` covers only the claimed-profile signal, so the MFA UI
   * reads this one.
   */
  professionalMfaRequired: boolean;

  // MFA state
  mfa: MfaState;

  // Supabase user data (from our DB, not Supabase Auth)
  supabaseUser: SupabaseUser | null;
  isSupabaseUserLoading: boolean;
  /** True once /users/me has completed at least once (success, empty, or
   *  error). Gates that depend on backend user fields (e.g. practiceEnabled)
   *  must wait for this, not just the loading flags, to avoid the pre-fetch
   *  race that briefly reads defaults as false (#6324). */
  isBackendUserResolved: boolean;
  supabaseUserError: string | null;

  // Combined user info for easy access. Issue #3355 PR 2e — `stripePlanName`
  // dropped from the public surface; consumers should use `plan` (resolved
  // via the catalog) or query `useSubscriptions` for richer state.
  user: {
    id: string;
    email: string;
    fullName: string;
    plan: string;
    stripeCustomerId: string | null;
    stripeProductId: string | null;
    subscriptionStatus: string | null;
    currentPeriodEnd: string | null;
    isProfessional: boolean;
    professionalId: string | null;
  } | null;

  // Auth actions
  requireAuth: (callback?: () => void) => boolean;
  refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const {
    session,
    user: authUser,
    isLoading: isAuthLoading,
  } = useSupabaseAuth();
  const isSignedIn = !!session;
  const isLoaded = !isAuthLoading;

  // Supabase user state (from our DB)
  const [supabaseUser, setSupabaseUser] = useState<SupabaseUser | null>(null);
  const [isSupabaseUserLoading, setIsSupabaseUserLoading] = useState(false);
  // True once the backend /users/me fetch has completed at least once (success,
  // empty, or error). Distinct from `isSupabaseUserLoading`, which starts false
  // BEFORE the fetch effect runs — that pre-fetch window is why a signed-in
  // advocate landing straight on a gated route (e.g. /kancelaria/spisy/1) could
  // be momentarily seen as `practiceEnabled=false` and wrongly redirected to
  // /chat (#6324 race). Gates must wait for this, not just the loading flags.
  const [isBackendUserResolved, setIsBackendUserResolved] = useState(false);
  const [supabaseUserError, setSupabaseUserError] = useState<string | null>(
    null
  );

  // Professional state
  const [professionalId, setProfessionalId] = useState<string | null>(null);
  const [isProfessional, setIsProfessional] = useState(false);

  // Fetch professional profile.
  //
  // This callback is INTENTIONALLY stable (empty deps). The earlier version
  // read ``isSignedIn`` to early-return, which created a closure-staleness
  // bug: ``fetchSupabaseUser`` captured a ``fetchProfessionalProfile``
  // instance from the first render (when ``isSignedIn`` was still false),
  // so even after auth resolved and the useEffect gate opened, the inner
  // call silently early-returned — leaving ``isProfessional=false`` for
  // users who *did* have a claimed profile.
  //
  // Caller contract: the useEffect below only calls ``fetchSupabaseUser``
  // (which triggers this) when ``isSignedIn && authUser?.id``. So we can
  // safely assume there's a session at call time. If that assumption is
  // ever wrong (logout mid-flight), the API returns 401 and the catch
  // arm handles it — no local early-return needed.
  //
  // Returns whether the profile RESOLVED (found or a definitive "not a
  // professional" 404 the api interceptor resolves) vs. threw. A thrown call
  // (transient 5xx / network) returns false so the bootstrap can retry instead
  // of latching `isProfessional=false` and hiding the practice sidebar (#7202).
  const fetchProfessionalProfile = useCallback(async (): Promise<boolean> => {
    try {
      const professionalsService = (
        await import('@/services/professionals-services')
      ).default;
      const response = await professionalsService.getMyProfile();
      // eslint-disable-next-line no-console
      const data = response?.data;
      if (data?.id) {
        setProfessionalId(data.id);
        setIsProfessional(true);
      } else {
        setProfessionalId(null);
        setIsProfessional(false);
      }
      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(
        '[auth-context] getMyProfile threw → isProfessional=false',
        err
      );
      setProfessionalId(null);
      setIsProfessional(false);
      return false;
    }
  }, []);

  const abortControllerRef = useRef<AbortController | null>(null);

  // Fetch user data via backend API.
  //
  // Returns whether this attempt reached a definitive result (so the bootstrap
  // in `useResilientBootstrap` can stop retrying) vs. a transient failure that
  // should be retried. `true` only when getCurrentUser resolved AND the
  // professional-profile call resolved without a transient throw (#7202).
  const fetchSupabaseUser = useCallback(async (): Promise<boolean> => {
    if (isSupabaseUserLoading) {
      return false;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    try {
      setIsSupabaseUserLoading(true);
      setSupabaseUserError(null);

      const [response, profileResolved] = await Promise.all([
        usersServices.getCurrentUser(),
        fetchProfessionalProfile(),
      ]);

      if (abortControllerRef.current?.signal.aborted) {
        return false;
      }

      if (response.status === 'success' && response.data) {
        setSupabaseUser(response.data as SupabaseUser);
      } else {
        console.warn(
          '[Auth] User not found in DB, may need to create:',
          response.message
        );
        setSupabaseUser(null);
      }

      // getCurrentUser resolved to a definitive state (success, or a
      // non-transient 401/404 that the api interceptor resolves rather than
      // throws). The bootstrap is only complete when the professional-profile
      // call ALSO resolved without a transient throw — otherwise
      // `isProfessional` is wrongly pinned false and the practice sidebar
      // would latch hidden until a manual refresh (#7202).
      return profileResolved;
    } catch (err) {
      if (abortControllerRef.current?.signal.aborted) {
        return false;
      }

      console.error('Error fetching user data:', {
        error: err instanceof Error ? err.message : String(err),
      });
      setSupabaseUserError(
        err instanceof Error ? err.message : 'Unknown error'
      );
      setSupabaseUser(null);
      // Transient failure (getCurrentUser threw — 5xx / network) — retry.
      return false;
    } finally {
      if (!abortControllerRef.current?.signal.aborted) {
        setIsSupabaseUserLoading(false);
        setIsBackendUserResolved(true);
      }
    }
    // fetchProfessionalProfile is a stable useCallback([]) reference.
  }, [isSupabaseUserLoading, fetchProfessionalProfile]);

  // Refresh user data from DB
  const refreshUserData = useCallback(async () => {
    if (isSignedIn && authUser?.id) {
      await fetchSupabaseUser();
    }
  }, [isSignedIn, authUser?.id, fetchSupabaseUser]);

  // Bootstrap DB user + professional profile once auth resolves. Uses bounded
  // retry + window-focus self-heal so a transient/partial first-load failure
  // (e.g. a 5xx wave) doesn't latch a degraded state — an advocate's Moja
  // kancelária / Spisy sidebar staying hidden until a manual refresh (#7202).
  useResilientBootstrap({
    enabled: isSignedIn && !!authUser?.id,
    attempt: fetchSupabaseUser,
  });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Session expiry is handled by Supabase server-side:
  // - Time-box: 72h (3 days) — hard session lifetime
  // - Inactivity timeout: 24h — expires if no activity
  // When the token refresh fails, onAuthStateChange fires SIGNED_OUT automatically.

  // Active organization derived from JWT claims (NOT session.user.app_metadata).
  //
  // The Custom Access Token Hook enriches the JWT with org_id, org_role, etc.
  // at token issuance time. However, session.user.app_metadata reflects the
  // *stored* auth.users.raw_app_meta_data which the hook does NOT modify.
  // We must decode the JWT access_token to read the hook-injected claims.
  const jwtAppMetadata = useMemo(() => {
    if (!session?.access_token) return null;
    try {
      const payload = session.access_token.split('.')[1];
      if (!payload) return null;
      const decoded = JSON.parse(atob(payload)) as Record<string, unknown>;
      return decoded['app_metadata'] as Record<string, unknown> | undefined;
    } catch {
      return null;
    }
  }, [session?.access_token]);

  const orgId = (jwtAppMetadata?.['org_id'] as string) ?? undefined;
  const activeOrg: ActiveOrg | null = orgId
    ? {
        id: orgId,
        name: (jwtAppMetadata?.['org_name'] as string) ?? '',
        slug: (jwtAppMetadata?.['org_slug'] as string) ?? null,
        role: (jwtAppMetadata?.['org_role'] as string) ?? null,
        isAdmin: jwtAppMetadata?.['org_role'] === 'org:owner',
        mfaEnforced: jwtAppMetadata?.['org_require_mfa'] === true,
        mfaDeadline:
          typeof jwtAppMetadata?.['org_mfa_deadline'] === 'string'
            ? (jwtAppMetadata['org_mfa_deadline'] as string)
            : null,
      }
    : null;

  const professionalMfaRequired = readProfessionalMfaRequired(jwtAppMetadata);

  // ─── MFA / Assurance Level ─────────────────────────────────────────────
  const [mfa, setMfa] = useState<MfaState>({
    currentLevel: null,
    nextLevel: null,
    challengeRequired: false,
  });

  useEffect(() => {
    if (!isSignedIn) {
      setMfa({ currentLevel: null, nextLevel: null, challengeRequired: false });
      return;
    }

    let cancelled = false;

    async function checkAAL() {
      const supabase = createClient();
      // Refresh first to avoid stale AAL after re-login (Supabase bug #11383)
      await supabase.auth.refreshSession();
      const { data, error: aalError } =
        await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

      if (cancelled || aalError || !data) return;

      setMfa({
        currentLevel: (data.currentLevel as AuthAssuranceLevel) ?? null,
        nextLevel: (data.nextLevel as AuthAssuranceLevel) ?? null,
        challengeRequired:
          data.nextLevel === 'aal2' && data.currentLevel !== data.nextLevel,
      });
    }

    void checkAAL();
    return () => {
      cancelled = true;
    };
  }, [isSignedIn, authUser?.id]);

  // Combined user object for easy access.
  // ``plan`` is the user's ``plan_tag`` (catalog source of truth), defaulting
  // to ``'trial'`` when not yet set — that's the entry tier every new user
  // lands on. Was hardcoded to ``'free'`` pre-#3322 and pre-trial-rename;
  // consumers reading this value to gate features now see the real catalog
  // tag (``trial`` / ``starter`` / ``plus``).
  const user = authUser
    ? {
        id: authUser.id,
        email: supabaseUser?.email || authUser.email || '',
        fullName:
          (authUser.user_metadata?.['full_name'] as string) ||
          supabaseUser?.full_name ||
          '',
        plan: supabaseUser?.plan_tag ?? 'trial',
        stripeCustomerId: supabaseUser?.stripe_customer_id || null,
        stripeProductId: supabaseUser?.stripe_product_id || null,
        subscriptionStatus: supabaseUser?.stripe_subscription_status || null,
        currentPeriodEnd: supabaseUser?.current_period_end || null,
        isProfessional,
        professionalId,
      }
    : null;

  // Auth helper function
  const requireAuth = (callback?: () => void) => {
    if (!isSignedIn && isLoaded) {
      return false;
    }

    if (callback && isSignedIn) {
      callback();
    }

    return isSignedIn || false;
  };

  const value: AuthContextType = {
    // Auth data
    isAuthenticated: isSignedIn || false,
    isLoading: !isLoaded,
    authUser,

    // Active organization
    activeOrg,

    // SAK MFA verdict from the JWT (#13125)
    professionalMfaRequired,

    // MFA state
    mfa,

    // Supabase user data (from our DB)
    supabaseUser,
    isSupabaseUserLoading,
    isBackendUserResolved,
    supabaseUserError,

    // Combined user info
    user,

    // Actions
    requireAuth,
    refreshUserData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
